import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Modal, type ModalProps } from '../Modal';

type ModalContent = Omit<ModalProps, 'open' | 'onClose'>;

interface ModalContextValue {
  /** Open a modal with the supplied content. Returns a closer function. */
  open: (content: ModalContent) => () => void;
  close: () => void;
}

const ModalContext = createContext<ModalContextValue | null>(null);

export function useModal(): ModalContextValue {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error('useModal must be used within <ModalProvider>');
  return ctx;
}

/**
 * App-wide host for modal dialogs. Pages call `useModal().open({...})` and the
 * provider mounts a single shared <Modal/> at the root.
 */
export function ModalProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<ModalContent | null>(null);

  const close = useCallback(() => setContent(null), []);
  const open = useCallback((c: ModalContent) => {
    setContent(c);
    return close;
  }, [close]);

  const value = useMemo(() => ({ open, close }), [open, close]);

  return (
    <ModalContext.Provider value={value}>
      {children}
      <Modal {...(content ?? { title: '' })} open={!!content} onClose={close} />
    </ModalContext.Provider>
  );
}
